import { HttpStatusCode, isAxiosError } from 'axios';
import { HttpNetworkError as InertiaHttpNetworkError } from '@inertiajs/core';
import { HttpNetworkError, HttpResponseError } from 'laravel-precognition';

// Laravel's non-standard "Page Expired" status; already handled by the axios CSRF interceptor.
const LARAVEL_PAGE_EXPIRED = 419;
const IGNORED_STATUS_CODES = [HttpStatusCode.Unauthorized, LARAVEL_PAGE_EXPIRED];

export function shouldIgnoreError(error: unknown): boolean {
    return error instanceof InertiaHttpNetworkError || isIgnoredAxiosError(error) || isIgnoredPrecognitionError(error);
}

/**
 * Restricts Sentry to events whose innermost frame comes from our own chunks.
 *
 * Pass `import.meta.url` so the allow list follows the asset host rather than
 * hardcoding the CDN. Anything that isn't an absolute HTTP(S) URL yields an empty
 * list, which Sentry reads as "allow everything" — this degrades to no filtering,
 * never to dropping every event.
 */
export function getAssetAllowUrls(moduleUrl: string): string[] {
    let assetDirectory: URL;

    try {
        assetDirectory = new URL('.', moduleUrl);
    } catch {
        return [];
    }

    if (assetDirectory.protocol !== 'http:' && assetDirectory.protocol !== 'https:') {
        return [];
    }

    return [assetDirectory.href];
}

function isIgnoredAxiosError(error: unknown): boolean {
    if (!isAxiosError(error)) {
        return false;
    }

    if (error.code === 'ECONNABORTED') {
        return true;
    }

    if (error.code === 'ERR_NETWORK' && !error.response) {
        return true;
    }

    return error.response !== undefined && IGNORED_STATUS_CODES.includes(error.response.status);
}

function isIgnoredPrecognitionError(error: unknown): boolean {
    // Precognition uses its own network error class, separate from Inertia's.
    if (error instanceof HttpNetworkError) {
        return true;
    }

    if (!(error instanceof HttpResponseError)) {
        return false;
    }

    return IGNORED_STATUS_CODES.includes(error.response.status);
}
